import { useState } from "react";
import { UserIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface Props {
    time: string;
    question: string;
    answer: string | string[];
    candidateName?: string;
    candidateAvatar?: string;
    questionNumber?: number;
    interviewerName?: string;
    highlightedText?: string;
    isActive?: boolean;
    onClick?: () => void;
}

/** Two-letter initials from a full-name string (handles single-word names too). */
function getInitialsFromName(name?: string): string {
    if (!name) return "";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    return "";
}

export default function HighlightTranscript({
    time,
    question,
    answer,
    candidateName,
    candidateAvatar,
    questionNumber,
    interviewerName,
    highlightedText,
    isActive = false,
    onClick,
}: Props) {
    const [avatarBroken, setAvatarBroken] = useState(false);
    const showAvatarImg = !!candidateAvatar && !avatarBroken;
    const paragraphs: string[] = (Array.isArray(answer) ? answer : [answer])
        .map((p) => (p ?? "").trim())
        .filter((p) => p.length > 0);
    const trimmedQuestion = (question ?? "").trim();
    const hasQuestion = trimmedQuestion.length > 0;
    const hasAnswer = paragraphs.length > 0;
    const candidateLabel = (candidateName ?? "").trim() || "Candidate";
    const candidateInitials = getInitialsFromName(candidateName);

    if (!hasQuestion && !hasAnswer) return null;

    const renderHighlighted = (text: string) => {
        if (!highlightedText || !text.includes(highlightedText)) {
            return text;
        }
        const parts = text.split(highlightedText);
        return (
            <>
                {parts[0]}
                <span className="bg-yellow-100 px-1 border-b-2 border-yellow-400 font-medium">
                    {highlightedText}
                </span>
                {parts.slice(1).join(highlightedText)}
            </>
        );
    };

    return (
        <div
            className={cn(
                "py-4",
                onClick && "cursor-pointer"
            )}
            onClick={onClick}
        >
            {/* Centered time chip — visually separates Q&A pairs in the list */}
            <div className="flex items-center justify-center mb-3">
                <span className="text-[11px] font-medium text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full tabular-nums">
                    {time}
                </span>
            </div>

            <div className="space-y-2.5 px-2">
                {/* Interviewer question (left bubble) — only when there's a question */}
                {hasQuestion && (
                    <div className="flex items-end gap-2">
                        <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 shadow-sm bg-gradient-to-br from-blue-500 to-blue-600">
                            <img
                                src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                                alt="ourteam"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="max-w-[80%] flex flex-col items-start">
                            <span className="text-[10px] uppercase tracking-wider font-medium text-blue-700 mb-1 px-1">
                                {interviewerName ?? "Interviewer"}{questionNumber ? ` · Q${questionNumber}` : ""}
                            </span>
                            <div className="px-3.5 py-2 text-sm whitespace-pre-line shadow-sm bg-white border border-neutral-200 text-gray-900 rounded-2xl rounded-bl-sm">
                                {renderHighlighted(trimmedQuestion)}
                            </div>
                        </div>
                    </div>
                )}

                {/* Candidate answer (right bubble) — only when there's at least one non-empty paragraph */}
                {hasAnswer && (
                    <div className="flex items-end gap-2 justify-end">
                        <div className="max-w-[80%] flex flex-col items-end">
                            <span className="text-[10px] uppercase tracking-wider font-medium text-neutral-500 mb-1 px-1">
                                {candidateLabel}
                            </span>
                            <div className="space-y-1.5">
                                {paragraphs.map((p, i) => (
                                    <div
                                        key={i}
                                        className="px-3.5 py-2 text-sm whitespace-pre-line shadow-sm bg-blue-600 text-white rounded-2xl rounded-br-sm"
                                    >
                                        {renderHighlighted(p)}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br from-neutral-700 to-neutral-800 text-white">
                            {showAvatarImg ? (
                                <img
                                    src={candidateAvatar}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={() => setAvatarBroken(true)}
                                />
                            ) : candidateInitials ? (
                                <span className="text-[10px] font-semibold leading-none">
                                    {candidateInitials}
                                </span>
                            ) : (
                                <UserIcon className="w-3.5 h-3.5" />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
